import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const LANGUAGES = {
  en: { code: 'en', name: 'English', short: 'EN' },
  zh_CN: { code: 'zh_CN', name: '简体中文', short: '简' },
  zh_TW: { code: 'zh_TW', name: '繁體中文', short: '繁' },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || translations['en'][keys[0]]?.[keys[1]] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    nav: {
      home: 'Home',
      loanPrograms: 'Loan Programs',
      howItWorks: 'How It Works',
      about: 'About',
      contact: 'Contact',
      getFunded: 'Get Funded',
    },
    hero: {
      tagline: 'Private Capital for Real Estate',
      title1: 'Fast, Reliable',
      title2: 'Hard Money',
      title3: 'Lending',
      description: 'Gann Private Lending provides private capital solutions for real estate investors. Close in as few as 5-7 business days with up to 75% LTV financing.',
      dayClosing: 'Day Closing',
      maxLtv: 'Max LTV',
      funded: 'Funded',
      getFundedToday: 'Get Funded Today',
    },
    quote: {
      title: 'Get Your Quote',
      subtitle: 'Submit your deal for a fast response within 24 hours.',
      fullName: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      loanType: 'Loan Type',
      selectLoanType: 'Select loan type',
      propertyAddress: 'Property Address',
      purchasePrice: 'Purchase Price / Property Value',
      loanAmount: 'Requested Loan Amount',
      additionalDetails: 'Additional Details',
      additionalPlaceholder: 'Tell us about your project, timeline, and experience...',
      submit: 'Submit Loan Inquiry',
      submitting: 'Submitting...',
      consent: 'By submitting this form, you consent to be contacted by Gann Private Lending regarding your loan inquiry.',
      thankYou: 'Thank You',
      received: 'Your loan inquiry has been received. A specialist will contact you within 24 hours.',
      callNow: 'Or call now:',
    },
    loanTypes: {
      fixFlip: 'Fix & Flip',
      bridge: 'Bridge Loan',
      rental: 'Rental Property',
      commercial: 'Commercial',
      refinance: 'Refinance / Cash-Out',
      foreclosure: 'Foreclosure Bail Out',
      eviction: 'Eviction Defense',
    },
    trustStrip: {
      trusted: 'Trusted by investors nationwide',
      licensed: 'Licensed Lender',
      directCapital: 'Direct Capital',
      fastDecisions: 'Fast Decisions',
    },
    valueProps: {
      title: 'Capital When You Need It',
      subtitle: 'Why Choose Us',
      description: 'We understand that timing is everything in real estate. Our streamlined process delivers fast, reliable funding for your investment projects.',
      dayClosing: '5-7 Day Closing',
      dayClosingDesc: 'Fast, reliable funding when you need it. No delays, no surprises.',
      ltv: 'Up to 75% LTV',
      ltvDesc: 'Competitive leverage for qualified borrowers and properties.',
      propertyTypes: 'All Property Types',
      propertyTypesDesc: 'Residential and commercial real estate. Single family to multifamily.',
      assetBased: 'Asset-Based',
      assetBasedDesc: 'We focus on the deal, not endless paperwork. Streamlined approval.',
    },
    loanPrograms: {
      title: 'Financing Solutions for Every Deal',
      subtitle: 'Loan Programs',
      description: 'From fix-and-flip to long-term rentals, we offer tailored loan programs to fit your investment strategy.',
      viewAll: 'View All Loan Programs',
      fixFlipTitle: 'Fix & Flip Loans',
      fixFlipDesc: 'Short-term financing for renovation projects. Purchase, rehab, and sell.',
      bridgeTitle: 'Bridge Loans',
      bridgeDesc: 'Quick capital to bridge the gap between transactions.',
      rentalTitle: 'Rental Property Loans',
      rentalDesc: 'Long-term financing for buy-and-hold investment properties.',
      commercialTitle: 'Commercial Loans',
      commercialDesc: 'Capital for commercial real estate investments and development.',
      refinanceTitle: 'Refinance / Cash-Out',
      refinanceDesc: 'Unlock equity from your existing investment properties.',
      foreclosureTitle: 'Foreclosure Bail Out',
      foreclosureDesc: 'Stop foreclosure fast. We can close in as little as 1 week to save your property.',
      evictionTitle: 'Real Estate Legal Services',
      evictionDesc: 'Eviction Defense - Protect your property rights with professional legal support. $2,000 flat fee.',
    },
    process: {
      title: 'How It Works',
      subtitle: 'Simple Process',
      description: 'Our streamlined process gets you from inquiry to funding in as few as 5-7 business days.',
      step1Title: 'Submit Your Deal',
      step1Desc: 'Complete our simple inquiry form with property and project details.',
      step2Title: 'Asset Review',
      step2Desc: 'Our team evaluates the property and issues preliminary terms within 24-48 hours.',
      step3Title: 'Documentation',
      step3Desc: 'Provide required documents. We handle appraisal and title coordination.',
      step4Title: 'Fund & Close',
      step4Desc: 'Sign documents and receive funding. Typical closing in 5-7 business days.',
    },
    cta: {
      title: 'Ready to Fund Your Next Deal?',
      description: 'Submit your loan inquiry today and receive a response within 24 hours. Our team is standing by to help you close your next investment.',
      getFundedNow: 'Get Funded Now',
      call: 'Call',
      responseTime: 'Response Time',
      loansFunded: 'Loans Funded',
    },
    footer: {
      description: 'Private capital solutions for real estate investors. Fast, reliable funding backed by decades of lending experience.',
      quickLinks: 'Quick Links',
      getFunded: 'Get Funded',
      getFundedDesc: 'Ready to fund your next real estate deal? Submit your loan inquiry today.',
      submitInquiry: 'Submit Inquiry',
      disclaimer: 'Important Disclosure: Gann Private Lending is a private lender and not a bank. All loans are subject to underwriting approval and collateral review. Loan terms, rates, and conditions vary based on property type, borrower experience, and market conditions. This is not a commitment to lend. No guarantee of approval is expressed or implied. California Department of Real Estate License #[License Number]. Equal Housing Lender.',
      rights: 'All rights reserved.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
    },
    about: {
      pageTitle: 'Private Capital Solutions for Professional Investors',
      pageSubtitle: 'About Gann Private Lending',
      pageDesc: 'Based in Los Angeles, Gann Private Lending provides fast, reliable private capital for real estate investors across the United States. We combine institutional discipline with the flexibility and speed of private lending.',
      missionSubtitle: 'Our Mission',
      missionTitle: 'Empowering Real Estate Investors with Reliable Capital',
      missionP1: 'At Gann Private Lending, we believe that access to capital should never be the limiting factor in a sound real estate investment. Our mission is to provide professional investors with fast, reliable funding so they can focus on what they do best – finding and executing profitable deals.',
      missionP2: 'We specialize in asset-based lending, which means we focus on the strength of the collateral property rather than extensive borrower documentation. This approach allows us to make quick decisions and close loans in days, not weeks.',
      missionP3: 'Whether you\'re a seasoned fix-and-flip investor, a growing landlord building a rental portfolio, or a developer taking on larger projects, we have the capital and expertise to support your investment strategy.',
      totalFunded: 'Total Loans Funded',
      successfulClosings: 'Successful Closings',
      avgDaysClose: 'Average Days to Close',
      responseTime: 'Response Time',
      valuesSubtitle: 'Our Values',
      valuesTitle: 'What Sets Us Apart',
      speedTitle: 'Speed & Efficiency',
      speedDesc: 'We understand that timing is critical in real estate. Our streamlined process delivers funding when you need it.',
      reliabilityTitle: 'Reliability',
      reliabilityDesc: 'When we commit to a deal, we close. No surprises, no last-minute changes. You can count on us.',
      partnershipTitle: 'Partnership Approach',
      partnershipDesc: 'We view our borrowers as partners. Your success is our success, and we work to build long-term relationships.',
      expertiseTitle: 'Expertise',
      expertiseDesc: 'Our team brings decades of combined experience in real estate lending and investment.',
      borrowersSubtitle: 'Who We Serve',
      borrowersTitle: 'Our Borrowers',
      borrowersDesc: 'We work with a diverse range of real estate professionals who need fast, reliable capital for their investment projects.',
      contactSubtitle: 'Contact Us',
      contactTitle: 'Get in Touch',
      contactDesc: 'Ready to discuss your next deal? Our team is here to help you find the right financing solution for your investment.',
      phone: 'Phone',
      email: 'Email',
      office: 'Office',
      submitDealToday: 'Submit Your Deal Today',
      submitDealDesc: 'Get a response within 24 hours. Our team is ready to review your project and provide competitive terms.',
    },
    loans: {
      pageTitle: 'Financing Solutions for Real Estate Investors',
      pageSubtitle: 'Loan Programs',
      pageDesc: 'From fix-and-flip to long-term rentals, Gann Private Lending offers tailored loan programs designed to meet the needs of professional real estate investors.',
      fastClosing: 'Fast Closing',
      ltvAvailable: 'LTV Available',
      loanAmounts: 'Loan Amounts',
      loanTerms: 'Loan Terms',
      loanAmount: 'Loan Amount',
      ltv: 'LTV',
      term: 'Term',
      closing: 'Closing',
      programFeatures: 'Program Features',
      applyFor: 'Apply for',
      termsDisclaimer: 'Terms shown are for qualified borrowers and properties. Actual terms may vary based on underwriting review.',
      notSure: 'Not Sure Which Loan is Right for You?',
      notSureDesc: 'Speak with one of our loan specialists to discuss your project and find the best financing solution for your investment strategy.',
      submitYourDeal: 'Submit Your Deal',
    },
    howItWorks: {
      pageTitle: 'From Inquiry to Funding in Days, Not Months',
      pageSubtitle: 'Our Process',
      pageDesc: 'Our streamlined process is designed for experienced real estate investors who need fast, reliable capital. No bureaucracy, no endless paperwork – just efficient lending.',
      whatsInvolved: 'What\'s Involved:',
      step1Timeline: 'Takes about 5 minutes',
      step2Timeline: '24-48 hour response',
      step3Timeline: '3-5 business days',
      step4Timeline: 'Closing in 5-7 days',
      step1Subtitle: 'Quick & Simple Application',
      step2Subtitle: 'Fast Evaluation Process',
      step3Subtitle: 'Streamlined Underwriting',
      step4Subtitle: 'Get Your Capital',
      step1Desc: 'Complete our streamlined loan inquiry form with your property and project details. We only ask for essential information to get started.',
      step2Desc: 'Our team reviews your deal and the collateral property. Within 24-48 hours, you receive preliminary loan terms including rate, LTV, and estimated costs.',
      step3Desc: 'Once you accept terms, we coordinate appraisal and title work. Our asset-based underwriting focuses on the property, not endless paperwork.',
      step4Desc: 'Sign your loan documents and receive funding. We wire directly to escrow or title company. You can close in as few as 5-7 business days from application.',
      advantageSubtitle: 'Why Work With Us',
      advantageTitle: 'The Gann Private Lending Advantage',
      speedTitle: 'Speed',
      speedDesc: 'Close in 5-7 business days. We understand timing matters in real estate.',
      directTitle: 'Direct Lender',
      directDesc: 'No brokers or middlemen. Work directly with decision makers.',
      certaintyTitle: 'Certainty',
      certaintyDesc: 'Clear terms upfront. No surprises at closing. Reliable capital.',
      readyTitle: 'Ready to Get Started?',
      readyDesc: 'Submit your deal today and receive a response within 24 hours. Our team is ready to help fund your next investment.',
    },
    contact: {
      pageTitle: 'Submit Your Loan Inquiry',
      pageSubtitle: 'Get Funded',
      pageDesc: 'Complete the form below to request financing for your real estate investment. Our team will review your deal and respond within 24 hours.',
      formTitle: 'Loan Inquiry Form',
      formDesc: 'Provide details about your property and financing needs. All fields marked with * are required.',
      contactInfo: 'Contact Information',
      phone: 'Phone',
      email: 'Email',
      office: 'Office',
      businessHours: 'Business Hours',
      businessHoursValue: 'Mon - Fri: 9:00 AM - 6:00 PM PST',
      whatToExpect: 'What to Expect',
      expect1: 'Response within 24 hours with preliminary feedback',
      expect2: 'Term sheet with rate, LTV, and costs within 48 hours',
      expect3: 'No obligation to proceed after receiving terms',
      expect4: 'Dedicated loan specialist assigned to your deal',
      loanHighlights: 'Loan Highlights',
      closingTime: 'Closing Time',
      maxLtv: 'Max LTV',
      loanRange: 'Loan Range',
      propertyTypes: 'Property Types',
      resComm: 'Res & Comm',
    },
    borrowerTypes: {
      fixFlip: 'Fix & Flip Investors',
      fixFlipDesc: 'Investors who purchase, renovate, and sell properties for profit.',
      buyHold: 'Buy & Hold Landlords',
      buyHoldDesc: 'Investors building rental portfolios for long-term cash flow and appreciation.',
      commercial: 'Commercial Investors',
      commercialDesc: 'Professionals investing in multifamily, retail, and commercial properties.',
      developers: 'Real Estate Developers',
      developersDesc: 'Developers taking on ground-up construction and major renovation projects.',
      brokers: 'Mortgage Brokers',
      brokersDesc: 'Brokers sourcing private capital solutions for their investor clients.',
      foreign: 'Foreign Nationals',
      foreignDesc: 'International investors acquiring U.S. real estate assets.',
    },
    loanFeatures: {
      fixFlip: ['Up to 90% of purchase', 'Rehab funds included', '12-24 month terms'],
      bridge: ['Fast approval', 'Flexible terms', 'Interest-only options'],
      rental: ['30-year terms available', 'Cash flow focused', 'Portfolio lending'],
      commercial: ['Mixed-use properties', 'Retail & office', 'Multifamily 5+ units'],
      refinance: ['Competitive rates', 'Quick closing', 'No prepayment penalties'],
      foreclosure: ['Close in 1 week', 'Stop foreclosure fast', 'Save your property'],
      eviction: ['$2,000 flat fee', 'Expert legal defense', 'Protect your rights'],
    },
  },
  zh_CN: {
    nav: {
      home: '首页',
      loanPrograms: '贷款方案',
      howItWorks: '申请流程',
      about: '关于我们',
      contact: '联系我们',
      getFunded: '立即申请',
    },
    hero: {
      tagline: '美国房产私募融资专家',
      title1: '极速放款',
      title2: '私募',
      title3: '贷款',
      description: 'Gann Private Lending 专注于为房产投资者提供灵活的私募融资方案。最快5-7个工作日放款，贷款成数高达75%。',
      dayClosing: '个工作日放款',
      maxLtv: '最高贷款成数',
      funded: '累计放款额',
      getFundedToday: '立即获取方案',
    },
    quote: {
      title: '快速获取报价',
      subtitle: '提交项目信息，我们将在24小时内回复您。',
      fullName: '您的姓名',
      email: '电子邮箱',
      phone: '联系电话',
      loanType: '贷款类型',
      selectLoanType: '请选择贷款类型',
      propertyAddress: '房产地址',
      purchasePrice: '购买价格 / 房产估值',
      loanAmount: '期望贷款金额',
      additionalDetails: '项目说明',
      additionalPlaceholder: '请简要介绍您的投资项目、计划周期及相关经验...',
      submit: '提交申请',
      submitting: '正在提交...',
      consent: '提交即表示您同意我们就贷款事宜与您联系。',
      thankYou: '感谢您的咨询',
      received: '我们已收到您的申请，专属顾问将在24小时内与您联系。',
      callNow: '或直接致电：',
    },
    loanTypes: {
      fixFlip: '房屋翻新贷款',
      bridge: '过桥贷款',
      rental: '出租房贷款',
      commercial: '商业地产贷款',
      refinance: '再融资/套现',
      foreclosure: '法拍房解救贷款',
      eviction: '驱逐辩护',
    },
    trustStrip: {
      trusted: '深受全美投资者信赖',
      licensed: '持牌金融机构',
      directCapital: '自有资金放款',
      fastDecisions: '快速审批决策',
    },
    valueProps: {
      title: '资金，在您需要的时候',
      subtitle: '为什么选择我们',
      description: '房产投资，时机就是一切。我们深谙此道，因此打造了高效的审批流程，确保您的项目不会因为资金问题而错失良机。',
      dayClosing: '5-7天极速放款',
      dayClosingDesc: '告别漫长等待，资金快速到位，让您抢占市场先机。',
      ltv: '最高75%贷款成数',
      ltvDesc: '为优质项目和借款人提供充足的资金杠杆。',
      propertyTypes: '房产类型全覆盖',
      propertyTypesDesc: '无论是独栋别墅还是多单元公寓，住宅商业均可申请。',
      assetBased: '看房产，不看收入',
      assetBasedDesc: '我们更关注房产本身的价值，简化繁琐的收入证明流程。',
    },
    loanPrograms: {
      title: '多元化贷款方案，满足您的投资需求',
      subtitle: '贷款方案',
      description: '从短期翻新到长期持有，我们提供量身定制的融资方案，助力您的每一笔投资。',
      viewAll: '查看全部方案',
      fixFlipTitle: '房屋翻新贷款',
      fixFlipDesc: '专为"买入-翻新-出售"策略设计的短期融资方案。',
      bridgeTitle: '过桥贷款',
      bridgeDesc: '解决交易间的资金周转需求，让您不错过任何好项目。',
      rentalTitle: '出租房贷款',
      rentalDesc: '为长期持有的租赁房产提供稳定的融资支持。',
      commercialTitle: '商业地产贷款',
      commercialDesc: '公寓楼、商铺、办公楼等商业地产投资融资。',
      refinanceTitle: '再融资/套现',
      refinanceDesc: '盘活现有房产资产，释放资金用于新的投资。',
      foreclosureTitle: '法拍房解救贷款',
      foreclosureDesc: '紧急阻止法拍。最快一周内放款，保住您的房产。',
      evictionTitle: '房产法律服务',
      evictionDesc: '驱逐辩护 - 专业法律支持，保护您的房产权益。固定收费 $2,000。',
    },
    process: {
      title: '申请流程',
      subtitle: '简单四步',
      description: '从提交申请到资金到账，最快只需5-7个工作日。',
      step1Title: '提交项目',
      step1Desc: '在线填写简单的申请表，提供房产和项目基本信息。',
      step2Title: '项目评估',
      step2Desc: '我们的团队会在24-48小时内评估房产并给出初步方案。',
      step3Title: '材料审核',
      step3Desc: '提交必要文件，我们负责安排评估和产权调查。',
      step4Title: '签约放款',
      step4Desc: '签署贷款文件，资金直接打入过户公司账户。',
    },
    cta: {
      title: '准备好启动您的下一个项目了吗？',
      description: '立即提交贷款咨询，我们将在24小时内给您答复。专业团队随时为您的投资保驾护航。',
      getFundedNow: '立即获取资金',
      call: '致电咨询',
      responseTime: '响应时间',
      loansFunded: '已完成项目',
    },
    footer: {
      description: '专注于房产投资融资，凭借丰富的行业经验，为投资者提供快速、可靠的资金支持。',
      quickLinks: '快速导航',
      getFunded: '立即申请',
      getFundedDesc: '有好项目？立即提交，让我们帮您实现投资目标。',
      submitInquiry: '提交申请',
      disclaimer: '重要声明：Gann Private Lending 为私人贷款机构，非银行。所有贷款需经审批，以抵押物评估为准。贷款条件因房产类型、借款人资质及市场情况而异。本页面内容不构成贷款承诺或批准保证。加州房地产部执照 #[执照号码]。平等住房贷款机构。',
      rights: '版权所有',
      privacy: '隐私政策',
      terms: '服务条款',
    },
    about: {
      pageTitle: '专业投资者的融资伙伴',
      pageSubtitle: '关于我们',
      pageDesc: 'Gann Private Lending 总部位于洛杉矶，专注于为全美房产投资者提供快速、灵活的私募融资服务。我们兼具专业机构的严谨与私募贷款的高效。',
      missionSubtitle: '我们的使命',
      missionTitle: '让资金不再成为您投资路上的障碍',
      missionP1: '我们始终相信，一个好的投资项目不应该因为资金问题而搁浅。我们的使命是为专业投资者提供快速、可靠的融资支持，让您专注于发现和把握每一个投资机会。',
      missionP2: '我们采用"看房产、轻文件"的审批模式，重点评估抵押房产的价值，而非繁琐的收入证明。这让我们能够快速做出决策，在数天内完成放款。',
      missionP3: '无论您是资深的翻新投资者、正在扩展房产组合的房东，还是承接大型开发项目的开发商，我们都能提供匹配的资金方案。',
      totalFunded: '累计放款总额',
      successfulClosings: '成功完成项目',
      avgDaysClose: '平均放款周期',
      responseTime: '响应时间',
      valuesSubtitle: '核心优势',
      valuesTitle: '为什么选择我们',
      speedTitle: '速度至上',
      speedDesc: '房产投资，时间就是金钱。我们的高效流程确保资金及时到位。',
      reliabilityTitle: '说到做到',
      reliabilityDesc: '一旦承诺，必定完成。没有意外，没有临时变卦，让您安心交易。',
      partnershipTitle: '伙伴关系',
      partnershipDesc: '我们把每一位借款人视为长期合作伙伴，您的成功就是我们的成功。',
      expertiseTitle: '专业经验',
      expertiseDesc: '团队成员深耕房产金融领域多年，经验丰富，值得信赖。',
      borrowersSubtitle: '服务对象',
      borrowersTitle: '我们服务的投资者',
      borrowersDesc: '我们与各类房产投资专业人士合作，为他们的项目提供资金支持。',
      contactSubtitle: '联系我们',
      contactTitle: '与我们取得联系',
      contactDesc: '想了解更多？我们的团队随时为您解答疑问，提供专业建议。',
      phone: '电话',
      email: '邮箱',
      office: '办公地址',
      submitDealToday: '立即提交您的项目',
      submitDealDesc: '24小时内获得专业反馈，我们的团队已准备好为您的项目提供支持。',
    },
    loans: {
      pageTitle: '为房产投资者量身打造的融资方案',
      pageSubtitle: '贷款方案',
      pageDesc: '从短期翻新到长期持有，Gann Private Lending 提供全方位的贷款产品，满足专业投资者的多元化需求。',
      fastClosing: '极速放款',
      ltvAvailable: '贷款成数',
      loanAmounts: '贷款额度',
      loanTerms: '贷款条款',
      loanAmount: '贷款额度',
      ltv: '贷款成数',
      term: '贷款期限',
      closing: '放款周期',
      programFeatures: '方案亮点',
      applyFor: '申请',
      termsDisclaimer: '以上条款适用于符合条件的借款人及房产，实际条款以审批结果为准。',
      notSure: '不确定哪种方案适合您？',
      notSureDesc: '联系我们的贷款顾问，详细了解您的项目后，我们将为您推荐最合适的融资方案。',
      submitYourDeal: '提交项目咨询',
    },
    howItWorks: {
      pageTitle: '从申请到放款，只需几天而非几个月',
      pageSubtitle: '申请流程',
      pageDesc: '我们的流程专为资深房产投资者设计——高效、透明、无繁文缛节，让您把时间花在真正重要的事情上。',
      whatsInvolved: '主要内容：',
      step1Timeline: '约5分钟',
      step2Timeline: '24-48小时内回复',
      step3Timeline: '3-5个工作日',
      step4Timeline: '5-7天内放款',
      step1Subtitle: '简单快捷',
      step2Subtitle: '专业评估',
      step3Subtitle: '高效审批',
      step4Subtitle: '资金到账',
      step1Desc: '在线填写贷款申请表，提供房产和项目的基本信息。我们只要求必要的信息，不会浪费您的时间。',
      step2Desc: '我们的团队会仔细评估您的项目和抵押房产，24-48小时内为您提供包含利率、贷款成数和预估费用的初步方案。',
      step3Desc: '确认方案后，我们会安排房产评估和产权调查。我们注重房产价值，简化文件要求。',
      step4Desc: '签署贷款文件，资金直接打入过户公司账户。从申请到放款，最快5-7个工作日。',
      advantageSubtitle: '选择我们的理由',
      advantageTitle: 'Gann Private Lending 的优势',
      speedTitle: '速度',
      speedDesc: '5-7个工作日完成放款，让您抢占市场先机。',
      directTitle: '直接放款',
      directDesc: '没有中间商，直接与决策者对话，沟通更高效。',
      certaintyTitle: '确定性',
      certaintyDesc: '条款清晰透明，签约前您就能清楚所有细节。',
      readyTitle: '准备好开始了吗？',
      readyDesc: '立即提交您的项目，24小时内获得专业反馈。我们的团队随时准备为您的投资助力。',
    },
    contact: {
      pageTitle: '提交贷款咨询',
      pageSubtitle: '立即申请',
      pageDesc: '填写以下表格，告诉我们您的项目情况。我们的团队将在24小时内与您联系。',
      formTitle: '贷款申请表',
      formDesc: '请填写您的房产和融资需求信息。标 * 为必填项。',
      contactInfo: '联系方式',
      phone: '电话',
      email: '邮箱',
      office: '办公地址',
      businessHours: '工作时间',
      businessHoursValue: '周一至周五 9:00-18:00（太平洋时间）',
      whatToExpect: '后续流程',
      expect1: '24小时内收到初步反馈',
      expect2: '48小时内收到包含利率和费用的详细方案',
      expect3: '收到方案后无任何签约义务',
      expect4: '专属贷款顾问全程跟进',
      loanHighlights: '贷款亮点',
      closingTime: '放款周期',
      maxLtv: '最高贷款成数',
      loanRange: '贷款额度',
      propertyTypes: '房产类型',
      resComm: '住宅/商业',
    },
    borrowerTypes: {
      fixFlip: '翻新投资者',
      fixFlipDesc: '专注于买入、翻新、出售策略的投资人。',
      buyHold: '长期持有房东',
      buyHoldDesc: '构建租赁房产组合，追求长期现金流和增值的投资者。',
      commercial: '商业地产投资者',
      commercialDesc: '投资公寓楼、商铺、写字楼等商业地产的专业人士。',
      developers: '房地产开发商',
      developersDesc: '从事新建开发或大型翻新项目的开发商。',
      brokers: '贷款经纪人',
      brokersDesc: '为客户寻找私募融资渠道的专业经纪人。',
      foreign: '海外投资者',
      foreignDesc: '投资美国房产的国际投资者。',
    },
    loanFeatures: {
      fixFlip: ['最高可贷购买价90%', '含翻新资金', '12-24个月期限'],
      bridge: ['快速审批', '条款灵活', '可选只付利息'],
      rental: ['最长30年期限', '关注现金流', '可做组合贷款'],
      commercial: ['混合用途房产', '商铺/写字楼', '5户以上公寓'],
      refinance: ['利率有竞争力', '快速放款', '无提前还款罚金'],
      foreclosure: ['一周内放款', '紧急阻止法拍', '保住您的房产'],
      eviction: ['固定收费 $2,000', '专业法律辩护', '保护您的权益'],
    },
  },
  zh_TW: {
    nav: {
      home: '首頁',
      loanPrograms: '貸款方案',
      howItWorks: '申請流程',
      about: '關於我們',
      contact: '聯繫我們',
      getFunded: '立即申請',
    },
    hero: {
      tagline: '美國房產私募融資專家',
      title1: '極速放款',
      title2: '私募',
      title3: '貸款',
      description: 'Gann Private Lending 專注於為房產投資者提供靈活的私募融資方案。最快5-7個工作日放款，貸款成數高達75%。',
      dayClosing: '個工作日放款',
      maxLtv: '最高貸款成數',
      funded: '累計放款額',
      getFundedToday: '立即獲取方案',
    },
    quote: {
      title: '快速獲取報價',
      subtitle: '提交項目資訊，我們將在24小時內回覆您。',
      fullName: '您的姓名',
      email: '電子郵箱',
      phone: '聯繫電話',
      loanType: '貸款類型',
      selectLoanType: '請選擇貸款類型',
      propertyAddress: '房產地址',
      purchasePrice: '購買價格 / 房產估值',
      loanAmount: '期望貸款金額',
      additionalDetails: '項目說明',
      additionalPlaceholder: '請簡要介紹您的投資項目、計劃週期及相關經驗...',
      submit: '提交申請',
      submitting: '正在提交...',
      consent: '提交即表示您同意我們就貸款事宜與您聯繫。',
      thankYou: '感謝您的諮詢',
      received: '我們已收到您的申請，專屬顧問將在24小時內與您聯繫。',
      callNow: '或直接致電：',
    },
    loanTypes: {
      fixFlip: '房屋翻新貸款',
      bridge: '過橋貸款',
      rental: '出租房貸款',
      commercial: '商業地產貸款',
      refinance: '再融資/套現',
      foreclosure: '法拍房解救貸款',
      eviction: '驅逐辯護',
    },
    trustStrip: {
      trusted: '深受全美投資者信賴',
      licensed: '持牌金融機構',
      directCapital: '自有資金放款',
      fastDecisions: '快速審批決策',
    },
    valueProps: {
      title: '資金，在您需要的時候',
      subtitle: '為什麼選擇我們',
      description: '房產投資，時機就是一切。我們深諳此道，因此打造了高效的審批流程，確保您的項目不會因為資金問題而錯失良機。',
      dayClosing: '5-7天極速放款',
      dayClosingDesc: '告別漫長等待，資金快速到位，讓您搶佔市場先機。',
      ltv: '最高75%貸款成數',
      ltvDesc: '為優質項目和借款人提供充足的資金槓桿。',
      propertyTypes: '房產類型全覆蓋',
      propertyTypesDesc: '無論是獨棟別墅還是多單元公寓，住宅商業均可申請。',
      assetBased: '看房產，不看收入',
      assetBasedDesc: '我們更關注房產本身的價值，簡化繁瑣的收入證明流程。',
    },
    loanPrograms: {
      title: '多元化貸款方案，滿足您的投資需求',
      subtitle: '貸款方案',
      description: '從短期翻新到長期持有，我們提供量身定制的融資方案，助力您的每一筆投資。',
      viewAll: '查看全部方案',
      fixFlipTitle: '房屋翻新貸款',
      fixFlipDesc: '專為「買入-翻新-出售」策略設計的短期融資方案。',
      bridgeTitle: '過橋貸款',
      bridgeDesc: '解決交易間的資金周轉需求，讓您不錯過任何好項目。',
      rentalTitle: '出租房貸款',
      rentalDesc: '為長期持有的租賃房產提供穩定的融資支持。',
      commercialTitle: '商業地產貸款',
      commercialDesc: '公寓樓、商鋪、辦公樓等商業地產投資融資。',
      refinanceTitle: '再融資/套現',
      refinanceDesc: '盤活現有房產資產，釋放資金用於新的投資。',
      foreclosureTitle: '法拍房解救貸款',
      foreclosureDesc: '緊急阻止法拍。最快一週內放款，保住您的房產。',
      evictionTitle: '房產法律服務',
      evictionDesc: '驅逐辯護 - 專業法律支持，保護您的房產權益。固定收費 $2,000。',
    },
    process: {
      title: '申請流程',
      subtitle: '簡單四步',
      description: '從提交申請到資金到賬，最快只需5-7個工作日。',
      step1Title: '提交項目',
      step1Desc: '線上填寫簡單的申請表，提供房產和項目基本資訊。',
      step2Title: '項目評估',
      step2Desc: '我們的團隊會在24-48小時內評估房產並給出初步方案。',
      step3Title: '材料審核',
      step3Desc: '提交必要文件，我們負責安排評估和產權調查。',
      step4Title: '簽約放款',
      step4Desc: '簽署貸款文件，資金直接打入過戶公司賬戶。',
    },
    cta: {
      title: '準備好啟動您的下一個項目了嗎？',
      description: '立即提交貸款諮詢，我們將在24小時內給您答覆。專業團隊隨時為您的投資保駕護航。',
      getFundedNow: '立即獲取資金',
      call: '致電諮詢',
      responseTime: '響應時間',
      loansFunded: '已完成項目',
    },
    footer: {
      description: '專注於房產投資融資，憑藉豐富的行業經驗，為投資者提供快速、可靠的資金支持。',
      quickLinks: '快速導航',
      getFunded: '立即申請',
      getFundedDesc: '有好項目？立即提交，讓我們幫您實現投資目標。',
      submitInquiry: '提交申請',
      disclaimer: '重要聲明：Gann Private Lending 為私人貸款機構，非銀行。所有貸款需經審批，以抵押物評估為準。貸款條件因房產類型、借款人資質及市場情況而異。本頁面內容不構成貸款承諾或批准保證。加州房地產部執照 #[執照號碼]。平等住房貸款機構。',
      rights: '版權所有',
      privacy: '隱私政策',
      terms: '服務條款',
    },
    about: {
      pageTitle: '專業投資者的融資夥伴',
      pageSubtitle: '關於我們',
      pageDesc: 'Gann Private Lending 總部位於洛杉磯，專注於為全美房產投資者提供快速、靈活的私募融資服務。我們兼具專業機構的嚴謹與私募貸款的高效。',
      missionSubtitle: '我們的使命',
      missionTitle: '讓資金不再成為您投資路上的障礙',
      missionP1: '我們始終相信，一個好的投資項目不應該因為資金問題而擱淺。我們的使命是為專業投資者提供快速、可靠的融資支持，讓您專注於發現和把握每一個投資機會。',
      missionP2: '我們採用「看房產、輕文件」的審批模式，重點評估抵押房產的價值，而非繁瑣的收入證明。這讓我們能夠快速做出決策，在數天內完成放款。',
      missionP3: '無論您是資深的翻新投資者、正在擴展房產組合的房東，還是承接大型開發項目的開發商，我們都能提供匹配的資金方案。',
      totalFunded: '累計放款總額',
      successfulClosings: '成功完成項目',
      avgDaysClose: '平均放款週期',
      responseTime: '響應時間',
      valuesSubtitle: '核心優勢',
      valuesTitle: '為什麼選擇我們',
      speedTitle: '速度至上',
      speedDesc: '房產投資，時間就是金錢。我們的高效流程確保資金及時到位。',
      reliabilityTitle: '說到做到',
      reliabilityDesc: '一旦承諾，必定完成。沒有意外，沒有臨時變卦，讓您安心交易。',
      partnershipTitle: '夥伴關係',
      partnershipDesc: '我們把每一位借款人視為長期合作夥伴，您的成功就是我們的成功。',
      expertiseTitle: '專業經驗',
      expertiseDesc: '團隊成員深耕房產金融領域多年，經驗豐富，值得信賴。',
      borrowersSubtitle: '服務對象',
      borrowersTitle: '我們服務的投資者',
      borrowersDesc: '我們與各類房產投資專業人士合作，為他們的項目提供資金支持。',
      contactSubtitle: '聯繫我們',
      contactTitle: '與我們取得聯繫',
      contactDesc: '想了解更多？我們的團隊隨時為您解答疑問，提供專業建議。',
      phone: '電話',
      email: '郵箱',
      office: '辦公地址',
      submitDealToday: '立即提交您的項目',
      submitDealDesc: '24小時內獲得專業反饋，我們的團隊已準備好為您的項目提供支持。',
    },
    loans: {
      pageTitle: '為房產投資者量身打造的融資方案',
      pageSubtitle: '貸款方案',
      pageDesc: '從短期翻新到長期持有，Gann Private Lending 提供全方位的貸款產品，滿足專業投資者的多元化需求。',
      fastClosing: '極速放款',
      ltvAvailable: '貸款成數',
      loanAmounts: '貸款額度',
      loanTerms: '貸款條款',
      loanAmount: '貸款額度',
      ltv: '貸款成數',
      term: '貸款期限',
      closing: '放款週期',
      programFeatures: '方案亮點',
      applyFor: '申請',
      termsDisclaimer: '以上條款適用於符合條件的借款人及房產，實際條款以審批結果為準。',
      notSure: '不確定哪種方案適合您？',
      notSureDesc: '聯繫我們的貸款顧問，詳細了解您的項目後，我們將為您推薦最合適的融資方案。',
      submitYourDeal: '提交項目諮詢',
    },
    howItWorks: {
      pageTitle: '從申請到放款，只需幾天而非幾個月',
      pageSubtitle: '申請流程',
      pageDesc: '我們的流程專為資深房產投資者設計——高效、透明、無繁文縟節，讓您把時間花在真正重要的事情上。',
      whatsInvolved: '主要內容：',
      step1Timeline: '約5分鐘',
      step2Timeline: '24-48小時內回覆',
      step3Timeline: '3-5個工作日',
      step4Timeline: '5-7天內放款',
      step1Subtitle: '簡單快捷',
      step2Subtitle: '專業評估',
      step3Subtitle: '高效審批',
      step4Subtitle: '資金到賬',
      step1Desc: '線上填寫貸款申請表，提供房產和項目的基本資訊。我們只要求必要的資訊，不會浪費您的時間。',
      step2Desc: '我們的團隊會仔細評估您的項目和抵押房產，24-48小時內為您提供包含利率、貸款成數和預估費用的初步方案。',
      step3Desc: '確認方案後，我們會安排房產評估和產權調查。我們注重房產價值，簡化文件要求。',
      step4Desc: '簽署貸款文件，資金直接打入過戶公司賬戶。從申請到放款，最快5-7個工作日。',
      advantageSubtitle: '選擇我們的理由',
      advantageTitle: 'Gann Private Lending 的優勢',
      speedTitle: '速度',
      speedDesc: '5-7個工作日完成放款，讓您搶佔市場先機。',
      directTitle: '直接放款',
      directDesc: '沒有中間商，直接與決策者對話，溝通更高效。',
      certaintyTitle: '確定性',
      certaintyDesc: '條款清晰透明，簽約前您就能清楚所有細節。',
      readyTitle: '準備好開始了嗎？',
      readyDesc: '立即提交您的項目，24小時內獲得專業反饋。我們的團隊隨時準備為您的投資助力。',
    },
    contact: {
      pageTitle: '提交貸款諮詢',
      pageSubtitle: '立即申請',
      pageDesc: '填寫以下表格，告訴我們您的項目情況。我們的團隊將在24小時內與您聯繫。',
      formTitle: '貸款申請表',
      formDesc: '請填寫您的房產和融資需求資訊。標 * 為必填項。',
      contactInfo: '聯繫方式',
      phone: '電話',
      email: '郵箱',
      office: '辦公地址',
      businessHours: '工作時間',
      businessHoursValue: '週一至週五 9:00-18:00（太平洋時間）',
      whatToExpect: '後續流程',
      expect1: '24小時內收到初步反饋',
      expect2: '48小時內收到包含利率和費用的詳細方案',
      expect3: '收到方案後無任何簽約義務',
      expect4: '專屬貸款顧問全程跟進',
      loanHighlights: '貸款亮點',
      closingTime: '放款週期',
      maxLtv: '最高貸款成數',
      loanRange: '貸款額度',
      propertyTypes: '房產類型',
      resComm: '住宅/商業',
    },
    borrowerTypes: {
      fixFlip: '翻新投資者',
      fixFlipDesc: '專注於買入、翻新、出售策略的投資人。',
      buyHold: '長期持有房東',
      buyHoldDesc: '構建租賃房產組合，追求長期現金流和增值的投資者。',
      commercial: '商業地產投資者',
      commercialDesc: '投資公寓樓、商鋪、寫字樓等商業地產的專業人士。',
      developers: '房地產開發商',
      developersDesc: '從事新建開發或大型翻新項目的開發商。',
      brokers: '貸款經紀人',
      brokersDesc: '為客戶尋找私募融資渠道的專業經紀人。',
      foreign: '海外投資者',
      foreignDesc: '投資美國房產的國際投資者。',
    },
    loanFeatures: {
      fixFlip: ['最高可貸購買價90%', '含翻新資金', '12-24個月期限'],
      bridge: ['快速審批', '條款靈活', '可選只付利息'],
      rental: ['最長30年期限', '關注現金流', '可做組合貸款'],
      commercial: ['混合用途房產', '商鋪/寫字樓', '5戶以上公寓'],
      refinance: ['利率有競爭力', '快速放款', '無提前還款罰金'],
      foreclosure: ['一週內放款', '緊急阻止法拍', '保住您的房產'],
      eviction: ['固定收費 $2,000', '專業法律辯護', '保護您的權益'],
    },
  },
};

export default translations;
